import { useHandler } from "@/hooks";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { nanoid } from "nanoid";
import type { FpUserMessageCreate } from "../../../schemas/codegen";
import { useInputStore } from "../../state/stores";
import { api } from "../api/api";
import { chatWebSocketManager } from "../websocket";
import { useMessageManagement } from "./useMessageManagement";

export function useChatCreation() {
  const router = useRouter();
  const { addMessage, clearChatData } = useMessageManagement();
  const clearInput = useInputStore((state) => state.clearInput);
  const setMessage = useInputStore((state) => state.setMessage);

  const createChat = useMutation({
    mutationFn: (summary: string) => api.createChat({ summary }),
  });

  const createNewChat = useHandler(async (message: string) => {
    const messageId = nanoid();
    addMessage({
      id: messageId,
      sender: "user",
      content: message,
      status: "completed",
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    clearInput();

    try {
      const response = await createChat.mutateAsync(message.split("\n")[0]);
      const chatId = response.data.chat.id;

      const connection = chatWebSocketManager.getConnection(chatId);
      if (connection) {
        await connection.connect();

        const messageData: FpUserMessageCreate = {
          type: "user.message.create",
          messageId,
          content: message,
        };
        connection.send(messageData);
      }

      await router.navigate({
        to: "/codegen/$chatId",
        params: { chatId },
        replace: true,
      });
    } catch (error) {
      clearChatData();
      setMessage(message);
      throw error;
    }
  });

  return {
    createNewChat,
    isCreating: createChat.isPending,
  };
}
